import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Box, Button, Container } from '@material-ui/core';
import { Section, useObservable, usePageTitle, LoadingIndicator } from '@lib/common';
import {
  getBulkChangeBillingQuery,
  getBulkChangeBillingService,
  BulkChangeBillingRunDetail,
  BulkChangeBillingRunStatus,
} from 'app/state';
import { AuditLogs, BreadcrumbBackButton } from 'app/shared';
import { BulkChangeBillingRunSummary, BulkChangeBillingStatusTable } from '../components';

interface RouteParams {
  runId: string;
}

export const BulkChangeBillingRunDetailsPage: React.FC<RouteComponentProps<RouteParams>> = ({
  match,
}) => {
  const runId = match.params.runId;
  const run = useObservable(getBulkChangeBillingQuery().activeRun, 'async') as
    | BulkChangeBillingRunDetail
    | undefined;

  usePageTitle('Bulk Change Billing Run Details');

  useEffect(() => {
    getBulkChangeBillingService().getRunById(runId);

    return () => {
      getBulkChangeBillingService().selectRun('');
    };
  }, [runId]);

  const handleRefresh = useCallback(() => {
    getBulkChangeBillingService().getRunById(runId);
  }, [runId]);

  const isRunning =
    run?.status === BulkChangeBillingRunStatus.NotStarted ||
    run?.status === BulkChangeBillingRunStatus.Running;

  if (!run) {
    return <LoadingIndicator loadingText="Loading Run Details" fillContainer />;
  }

  return (
    <Container maxWidth="xl">
      <Section>
        <BreadcrumbBackButton destination="/bulk-change-billing" />
        <BulkChangeBillingRunSummary run={run} />
        {isRunning && (
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleRefresh}>
              Refresh Billing Run Status
            </Button>
          </Box>
        )}
        <Section title="Reservation Statuses" disableGutters>
          <BulkChangeBillingStatusTable statuses={run.statuses ?? []} />
        </Section>
        <Section title="Audit History" disableGutters>
          <AuditLogs scope="bulk-change-billing-run" id={runId} />
        </Section>
      </Section>
    </Container>
  );
};
